import { ServerScriptLogEntry } from "@noya-app/state-manager";
import React from "react";
import { ObjectInspector } from "react-inspector";
import { getStateInspectorTheme } from "../inspectorTheme";
import { StateInspectorButton } from "../StateInspectorButton";
import {
  StateInspectorDisclosureRowInner,
  StateInspectorDisclosureSection,
} from "../StateInspectorDisclosureSection";
import { StateInspectorRow } from "../StateInspectorRow";

const levelColors: Record<ServerScriptLogEntry["level"], string> = {
  error: "#f87171",
  warn: "#facc15",
  log: "#9ca3af",
};

function formatTimestamp(timestamp: number) {
  return new Date(timestamp).toLocaleTimeString();
}

export function ServerScriptLogsSection({
  showLogs,
  setShowLogs,
  colorScheme,
  logs,
  containerRef,
  paused,
  onTogglePaused,
  title = "Server Script Logs",
}: {
  showLogs: boolean;
  setShowLogs: (value: boolean) => void;
  colorScheme: "light" | "dark";
  logs: ServerScriptLogEntry[];
  containerRef: React.RefObject<HTMLDivElement | null>;
  paused: boolean;
  onTogglePaused: () => void;
  title?: string;
}) {
  const theme = getStateInspectorTheme(colorScheme);

  return (
    <StateInspectorDisclosureSection
      open={showLogs}
      setOpen={setShowLogs}
      title={title}
      colorScheme={colorScheme}
      right={
        <StateInspectorButton theme={theme} onClick={onTogglePaused}>
          {paused ? "Resume" : "Pause"}
        </StateInspectorButton>
      }
    >
      <StateInspectorDisclosureRowInner ref={containerRef}>
        {paused && (
          <div
            style={{
              padding: "4px 12px",
              fontSize: "11px",
              opacity: 0.7,
            }}
          >
            Log streaming paused
          </div>
        )}
        {logs.map((log) => (
          <StateInspectorRow key={log.id} colorScheme={colorScheme}>
            <div
              style={{
                display: "flex",
                gap: "6px",
                alignItems: "center",
                fontSize: "10px",
                color: "inherit",
                marginBottom: log.values.length ? "4px" : 0,
                textTransform: "uppercase",
              }}
            >
              <span>{formatTimestamp(log.timestamp)}</span>
              <span style={{ color: levelColors[log.level] }}>{log.level}</span>
              {(log.origin || log.target || log.mode) && (
                <span
                  style={{
                    fontFamily: "monospace",
                    textTransform: "none",
                    fontSize: "10px",
                    opacity: 0.75,
                  }}
                >
                  {[log.origin ?? log.target, log.mode]
                    .filter(Boolean)
                    .join(" / ")}
                </span>
              )}
              <span
                style={{
                  fontFamily: "monospace",
                  textTransform: "none",
                  fontSize: "10px",
                }}
              >
                {log.scriptId}
              </span>
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "4px",
              }}
            >
              {log.values.length > 0 ? (
                log.values.map((value, index) => (
                  <ObjectInspector
                    key={index}
                    data={value}
                    theme={theme}
                    // expandLevel={3}
                  />
                ))
              ) : (
                <span style={{ fontSize: "11px", opacity: 0.7 }}>No data</span>
              )}
            </div>
          </StateInspectorRow>
        ))}
        {!logs?.length && (
          <div
            style={{
              padding: "12px",
              fontSize: "12px",
              display: "flex",
              flexDirection: "column",
              gap: "4px",
            }}
          >
            <span>No logs received</span>
          </div>
        )}
      </StateInspectorDisclosureRowInner>
    </StateInspectorDisclosureSection>
  );
}
